import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiCalendar } from 'react-icons/fi';
import { useWorkshopSchedule } from '../hooks/useWorkshopSchedule';

/**
 * Workshop Timer Component
 * Displays countdown and workshop information with auto-update
 */
export default function WorkshopTimer({ className = '', showStatus = true }) {
  const { workshopDetails, countdownTarget } = useWorkshopSchedule();
  const [timeLeft, setTimeLeft] = useState({});

  // Countdown timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const difference = countdownTarget.getTime() - now.getTime();

      let calculatedTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };

      if (difference > 0) {
        calculatedTime.days = Math.floor(difference / (1000 * 60 * 60 * 24));
        calculatedTime.hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        calculatedTime.minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        calculatedTime.seconds = Math.floor((difference % (1000 * 60)) / 1000);
      }

      setTimeLeft(calculatedTime);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [countdownTarget]);

  return (
    <div className={`workshop-timer ${className}`}>
      {showStatus && (
        <motion.div
          className="workshop-status"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FiCalendar className="status-icon" />
          <span>{workshopDetails.status}</span>
        </motion.div>
      )}

      <motion.div
        className="workshop-info"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="workshop-date">
          <strong>{workshopDetails.formattedDate}</strong>
        </div>
        <div className="workshop-time">
          {workshopDetails.timeString}
        </div>
      </motion.div>

      <motion.div
        className="countdown-timer"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <FiClock className="clock-icon" />
        <span>Workshop Starts In: </span>
        <div className="timer-display">
          <div className="timer-unit">
            <motion.span
              className="timer-number"
              key={timeLeft.days}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {String(timeLeft.days || 0).padStart(2, '0')}
            </motion.span>
            <span className="timer-label">Days</span>
          </div>
          <div className="timer-unit">
            <motion.span
              className="timer-number"
              key={timeLeft.hours}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {String(timeLeft.hours || 0).padStart(2, '0')}
            </motion.span>
            <span className="timer-label">Hours</span>
          </div>
          <div className="timer-unit">
            <motion.span
              className="timer-number"
              key={timeLeft.minutes}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {String(timeLeft.minutes || 0).padStart(2, '0')}
            </motion.span>
            <span className="timer-label">Minutes</span>
          </div>
          <div className="timer-unit">
            <motion.span
              className="timer-number pulse-animation"
              key={timeLeft.seconds}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {String(timeLeft.seconds || 0).padStart(2, '0')}
            </motion.span>
            <span className="timer-label">Seconds</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}